import { useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Trash2, Loader2 } from "lucide-react";

export function DeleteClientButton({ client, onDeleted, variant = "icon" }) {
  const { user } = useAuth();
  const isMaster = user?.role === "master";
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [reason, setReason] = useState("");

  const act = async () => {
    setBusy(true);
    try {
      const { data } = await api.delete(`/clients/${client.id}`, { data: { reason } });
      if (data.status === "deleted") toast.success(`${client.name} deleted`);
      else toast.success(data.message || "Erase request sent to Master for approval");
      setOpen(false);
      setReason("");
      onDeleted?.(data.status);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not delete");
    } finally { setBusy(false); }
  };

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger asChild>
        {variant === "icon" ? (
          <button
            onClick={(e) => e.stopPropagation()}
            className="text-muted-foreground hover:text-destructive p-1.5 rounded-md hover:bg-destructive/10 transition-colors"
            data-testid={`delete-client-${client.id}`}
            title={isMaster ? "Delete client" : "Request erase"}
          >
            <Trash2 className="h-4 w-4" />
          </button>
        ) : (
          <Button
            variant="outline"
            className="rounded-lg text-destructive border-destructive/40 hover:bg-destructive/10"
            data-testid={`delete-client-${client.id}`}
          >
            <Trash2 className="h-4 w-4 mr-1" /> {isMaster ? "Delete Client" : "Request Erase"}
          </Button>
        )}
      </AlertDialogTrigger>
      <AlertDialogContent onClick={(e) => e.stopPropagation()}>
        <AlertDialogHeader>
          <AlertDialogTitle>{isMaster ? `Delete ${client.name}?` : `Request to erase ${client.name}?`}</AlertDialogTitle>
          <AlertDialogDescription>
            {isMaster
              ? "This permanently removes the client and all their negative items, letters, mailings, agreements and documents. This cannot be undone."
              : "This sends an erase request to the Master for approval. The client is only deleted after the Master approves it."}
          </AlertDialogDescription>
        </AlertDialogHeader>
        {!isMaster && (
          <div className="space-y-2">
            <Label>Reason for erase (optional)</Label>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Duplicate record / client requested removal / created in error"
              className="min-h-[80px]"
              data-testid="erase-reason"
            />
          </div>
        )}
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="delete-cancel">Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={(e) => { e.preventDefault(); act(); }}
            disabled={busy}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            data-testid="delete-confirm"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : isMaster ? "Delete" : "Send Request"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
